import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  currentUser,
  ensureAuthPersistence,
  signInWithGoogle,
  signOutUser,
  subscribeAuth,
  formatAuthError,
} from "../billing/firebaseAuth";
import { billingWebConfig, isFirebaseConfigured } from "../billing/billingConfig";
import { registerAccount, fetchLifecycle } from "../account/accountApiClient";
import { clearPendingConsent, readPendingConsent } from "../account/consent";
import {
  beginSession,
  clearSession,
  setIncarnation,
  captureScope,
  isCurrentScope,
  abortOwnedRequests,
} from "./authSession";

const AuthContext = createContext(null);
const CROSS_TAB_KEY = "stratify.auth.event";

export const AUTH_PHASES = Object.freeze({
  initializing: "initializing",
  signedOut: "signedOut",
  authenticating: "authenticating",
  lifecycleUnresolved: "lifecycleUnresolved",
  active: "active",
  deletionRequested: "deletionRequested",
  deleted: "deleted",
  error: "error",
});

function mapLifecyclePhase(lifecycle) {
  const state = String(lifecycle?.state || "").toLowerCase();
  if (state === "deleted") return AUTH_PHASES.deleted;
  if (state === "deletion_requested" || state === "deleting") {
    return AUTH_PHASES.deletionRequested;
  }
  if (state === "unresolved") return AUTH_PHASES.lifecycleUnresolved;
  if (state === "active" || lifecycle?.implicitActive) return AUTH_PHASES.active;
  if (lifecycle?.missingRecord) return AUTH_PHASES.lifecycleUnresolved;
  return AUTH_PHASES.active;
}

function unwrapLifecycle(json) {
  if (!json) return null;
  return json.lifecycle || json;
}

function broadcastAuthEvent(type) {
  try {
    localStorage.setItem(
      CROSS_TAB_KEY,
      JSON.stringify({ type, at: Date.now(), uid: currentUser()?.uid || null })
    );
  } catch {
    /* ignore quota */
  }
}

export function AuthProvider({ children }) {
  const [phase, setPhase] = useState(AUTH_PHASES.initializing);
  const [user, setUser] = useState(null);
  const [lifecycle, setLifecycle] = useState(null);
  const [error, setError] = useState(null);
  const resolvingRef = useRef(0);

  const resolveLifecycle = useCallback(async (nextUser) => {
    const ticket = ++resolvingRef.current;
    const scope = beginSession({
      projectId: billingWebConfig.firebase.projectId,
      uid: nextUser.uid,
    });
    setPhase(AUTH_PHASES.lifecycleUnresolved);
    setError(null);
    try {
      let life = unwrapLifecycle(await fetchLifecycle({ scope }));
      if (!isCurrentScope(scope) || ticket !== resolvingRef.current) return;
      const state = String(life?.state || "").toLowerCase();
      const missing = !life || life.missingRecord || state === "unresolved";
      if (missing && state !== "deleted") {
        const consent = readPendingConsent();
        try {
          await registerAccount({
            commandId: `register_${nextUser.uid}`,
            consent,
            scope,
          });
          if (consent) clearPendingConsent();
        } catch (registerErr) {
          if (registerErr?.code === "account_deleted") {
            if (!isCurrentScope(scope) || ticket !== resolvingRef.current) return;
            setLifecycle(life);
            setPhase(AUTH_PHASES.deleted);
            return;
          }
          throw registerErr;
        }
        life = unwrapLifecycle(await fetchLifecycle({ scope }));
      }
      if (!isCurrentScope(scope) || ticket !== resolvingRef.current) return;
      setLifecycle(life);
      setIncarnation(life?.incarnation ?? null);
      setPhase(mapLifecyclePhase(life));
    } catch (err) {
      if (err?.code === "stale_session" || err?.abort) return;
      if (!isCurrentScope(scope) || ticket !== resolvingRef.current) return;
      setError(err);
      setPhase(AUTH_PHASES.error);
    }
  }, []);

  useEffect(() => {
    let unsub = () => {};
    let cancelled = false;
    (async () => {
      if (!isFirebaseConfigured()) {
        clearSession();
        setUser(null);
        setLifecycle(null);
        setPhase(AUTH_PHASES.signedOut);
        return;
      }
      await ensureAuthPersistence();
      if (cancelled) return;
      unsub = subscribeAuth((next) => {
        if (!next) {
          abortOwnedRequests();
          clearSession();
          setUser(null);
          setLifecycle(null);
          setError(null);
          setPhase(AUTH_PHASES.signedOut);
          return;
        }
        setUser(next);
        resolveLifecycle(next);
      });
    })();
    return () => {
      cancelled = true;
      unsub();
    };
  }, [resolveLifecycle]);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key !== CROSS_TAB_KEY || !event.newValue) return;
      try {
        const payload = JSON.parse(event.newValue);
        if (payload.type === "signOut" && currentUser()) {
          signOutUser();
        }
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const signIn = useCallback(async () => {
    setPhase(AUTH_PHASES.authenticating);
    setError(null);
    try {
      await signInWithGoogle();
    } catch (err) {
      setError(err);
      setPhase((prev) => (currentUser() ? prev : AUTH_PHASES.signedOut));
      throw err;
    }
  }, []);

  const signOut = useCallback(async () => {
    abortOwnedRequests();
    clearSession();
    setUser(null);
    setLifecycle(null);
    setPhase(AUTH_PHASES.signedOut);
    broadcastAuthEvent("signOut");
    await signOutUser();
  }, []);

  const retryLifecycle = useCallback(async () => {
    if (user) await resolveLifecycle(user);
  }, [resolveLifecycle, user]);

  const value = useMemo(
    () => ({
      phase,
      ready: phase !== AUTH_PHASES.initializing,
      user,
      uid: user?.uid || null,
      email: user?.email || null,
      displayName: user?.displayName || null,
      lifecycle,
      incarnation: lifecycle?.incarnation ?? null,
      error,
      errorMessage: error ? formatAuthError(error) : null,
      signedIn: Boolean(user),
      isActive: phase === AUTH_PHASES.active,
      signIn,
      signOut,
      retryLifecycle,
      scope: captureScope(),
    }),
    [phase, user, lifecycle, error, signIn, signOut, retryLifecycle]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return ctx;
}

export function useOptionalAuth() {
  return useContext(AuthContext);
}
